import * as React from 'react';

import type { CycleCountScopeType } from '@rematter/frontend-lib-api-client';
import {
  Button,
  ConsoleDialog,
  NumberField,
  Select,
  Switch,
  TextArea,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { createCycleCountTaskAtom } from '../../_graphql/mutationCycleCountTask';
import { $remoteFacilities } from '../../_state/facilities.state';
import { useTranslations } from '../../i18n/useTranslations';

type CreateCycleCountTaskDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const COUNT_TYPE_KEYS = ['Full', 'Category', 'Zone', 'Abc', 'Value'] as const;

//
// ⚛️ ⚛️ ⚛️ CREATE CYCLE COUNT TASK DIALOG ⚛️ ⚛️ ⚛️
//
export const CreateCycleCountTaskDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: CreateCycleCountTaskDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [facilityId, setFacilityId] = React.useState('');
  const [countType, setCountType] = React.useState<CycleCountScopeType>('Full');
  const [dueDate, setDueDate] = React.useState<string>('');
  const [isBlindCount, setIsBlindCount] = React.useState(false);
  const [tolerancePercentage, setTolerancePercentage] =
    React.useState<number>(5);
  const [notes, setNotes] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );

  const createTask = useSetAtom(createCycleCountTaskAtom({}));

  const countTypeOptions = React.useMemo(
    () =>
      COUNT_TYPE_KEYS.map((key) => ({
        key,
        label: t.cycleCountTasksView.createTask.countTypes[key] ?? key,
      })),
    [t],
  );

  const handleClose = React.useCallback(() => {
    setFacilityId('');
    setCountType('Full' as CycleCountScopeType);
    setDueDate('');
    setIsBlindCount(false);
    setTolerancePercentage(5);
    setNotes('');
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (!facilityId) return;

    try {
      setIsSubmitting(true);
      await createTask({
        facilityId,
        countType,
        isBlindCount,
        dueDate: dueDate || undefined,
        notes: notes.trim() || undefined,
        defaultTolerancePercentage: tolerancePercentage,
      });
      toast.success(t.cycleCountTasksView.createTask.success);
      onSuccess?.();
      handleClose();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t.cycleCountTasksView.detail.actions.unknownError;
      toast.error(t.cycleCountTasksView.createTask.error(message));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    facilityId,
    countType,
    isBlindCount,
    dueDate,
    notes,
    tolerancePercentage,
    createTask,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const facilityOptions = React.useMemo(
    () => facilities.map((f) => ({ key: f.id, label: f.name })),
    [facilities],
  );

  const canSubmit = Boolean(facilityId) && !isSubmitting;

  return (
    <ConsoleDialog
      className="@CreateCycleCountTaskDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.cycleCountTasksView.createTask.dialogTitle}
      size="wrap"
      content={
        <div className="space-y-4">
          <Select
            label={t.cycleCountTasksView.createTask.facility}
            value={facilityId}
            onChange={(key) => setFacilityId(String(key))}
            options={facilityOptions}
            isRequired
          />

          <Select
            label={t.cycleCountTasksView.createTask.countType}
            value={countType}
            onChange={(key) => setCountType(String(key) as CycleCountScopeType)}
            options={countTypeOptions}
            isRequired
          />

          <TextField
            label={t.cycleCountTasksView.createTask.dueDate}
            value={dueDate}
            onChange={setDueDate}
            type="date"
          />

          <NumberField
            label={t.cycleCountTasksView.createTask.tolerancePercentage}
            value={tolerancePercentage}
            onChange={(value) => setTolerancePercentage(value ?? 5)}
            minValue={0}
            maxValue={100}
          />

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {t.cycleCountTasksView.createTask.blindCount}
            </span>
            <Switch isSelected={isBlindCount} onChange={setIsBlindCount} />
          </div>

          <TextArea
            label={t.cycleCountTasksView.createTask.notes}
            value={notes}
            onChange={setNotes}
            placeholder={t.cycleCountTasksView.createTask.notesPlaceholder}
          />
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.cycleCountTasksView.createTask.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {t.cycleCountTasksView.createTask.submit}
          </Button>
        </div>
      }
    />
  );
};
